import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    FaCheckCircle,
    FaExclamationTriangle,
    FaInfoCircle,
    FaTimesCircle,
    FaClock,
    FaCopy,
    FaServer,
    FaQrcode,
    FaFileInvoiceDollar
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import QRCode from "react-qr-code";
import { formatDateTime } from '../../../utils/FormateDateTime';
import { Skeleton } from 'primereact/skeleton';

const ZatcaHistoryModal = ({ historyData, loading }) => {
    const { t } = useTranslation();

    const InfoBox = ({ label, value, colorClass = "text-gray-800" }) => (
        <div className="bg-gray-50/50 p-2 rounded border border-gray-100">
            {loading ? (
                <Skeleton width="40%" height="12px" className="mb-2" />
            ) : (
                <h6 className="text-[12px] uppercase font-bold text-gray-500 leading-none mb-2">{label}</h6>
            )}
            {loading ? (
                <Skeleton width="100%" height="16px" />
            ) : (
                <p className={`text-[12px] font-semibold truncate leading-normal ${colorClass}`} title={value}>
                    {value || '--'}
                </p>
            )}
        </div>
    );

    const LogTable = ({ items, type, title, colorClass, icon: MIcon }) => {
        if (!items?.length && !loading) return null;
        return (
            <div className="mb-3">
                <div className={`flex items-center gap-2 mb-1 px-1`}>
                    {loading ? <Skeleton shape="circle" size="12px" /> : <MIcon size={12} className={colorClass} />}
                    {loading ? (
                        <Skeleton width="80px" height="11px" />
                    ) : (
                        <span className={`text-[11px] font-bold uppercase tracking-tight ${colorClass}`}>{title} ({items.length})</span>
                    )}
                </div>
                <div className="border border-gray-100 rounded overflow-hidden">
                    <table className="w-full text-[11px] border-collapse">
                        <thead>
                            <tr className="bg-gray-50 border-b border-gray-100">
                                <th className="p-1.5 text-left text-[10px] text-gray-400 font-bold uppercase w-1/4">
                                    {loading ? <Skeleton width="30%" height="10px" /> : t('Code')}
                                </th>
                                <th className="p-1.5 text-left text-[10px] text-gray-400 font-bold uppercase w-1/4">
                                    {loading ? <Skeleton width="40%" height="10px" /> : t('Category')}
                                </th>
                                <th className="p-1.5 text-left text-[10px] text-gray-400 font-bold uppercase">
                                    {loading ? <Skeleton width="50%" height="10px" /> : t('Details')}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {loading ? (
                                [1, 2, 3].map((_, idx) => (
                                    <tr key={idx}>
                                        <td className="p-1.5"><Skeleton width="90%" height="14px" /></td>
                                        <td className="p-1.5"><Skeleton width="90%" height="14px" /></td>
                                        <td className="p-1.5"><Skeleton width="90%" height="14px" /></td>
                                    </tr>
                                ))
                            ) : (
                                items.map((item, idx) => (
                                    <tr key={idx} className="hover:bg-gray-50 transition-colors">
                                        <td className={`p-1.5 align-top font-bold ${colorClass?.replace('text-', 'text-opacity-80 text-')}`}>{item.Code}</td>
                                        <td className="p-1.5 align-top text-gray-600 leading-normal font-medium">{item.Category}</td>
                                        <td className="p-1.5 align-top text-gray-600 leading-normal font-medium">{item.Message}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    if (!loading && !historyData?.data) {
        return (
            <div className="py-8 text-center text-gray-400 text-sm italic">
                {t('No ZATCA history available')}
            </div>
        );
    }

    const data = historyData?.data;
    const zatcaResponse = data?.zatca_response?.data;
    const reportingResult = zatcaResponse?.reportingresult ? JSON.parse(zatcaResponse.reportingresult) : null;
    const validationResults = reportingResult?.ValidationResults;
    const status = (zatcaResponse?.reportingstatus || 'UNKNOWN').toUpperCase();

    const handleCopy = (text) => {
        navigator.clipboard.writeText(text);
        toast.success(t('Copied to clipboard'));
    };

    return (
        <div className="max-h-[85vh] overflow-y-auto pr-1">

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                <InfoBox
                    label={t("Status")}
                    value={status}
                    colorClass={status === 'REPORTED' || status === 'PASS'||status === 'CLEARED' ? 'text-green-600' : 'text-red-600'}
                />
                <InfoBox label={t("Zatca ID")} value={zatcaResponse?.id} />
                <InfoBox
                    label={t("Submission")}
                    value={zatcaResponse?.submissiondate ? formatDateTime(new Date(zatcaResponse.submissiondate)) : null}
                />
                <InfoBox label={t("Clearance")} value={reportingResult?.ClearanceStatus} />
            </div>

            <div className="flex flex-col lg:flex-row gap-4">

                <div className="lg:w-1/3 space-y-3">
                    <div className="bg-white border border-gray-100 rounded p-3 text-center shadow-sm">
                        <div className="flex items-center justify-start gap-2 mb-3 pb-2 border-b border-gray-50">
                            {loading ? <Skeleton shape="circle" size="14px" /> : <FaQrcode className="text-gray-400" size={14} />}
                            {loading ? <Skeleton width="60px" height="12px" /> : <span className="text-[11px] font-bold text-gray-600 uppercase tracking-wide">{t("Scannable QR")}</span>}
                        </div>
                        <div className="bg-white p-2 rounded border border-gray-50 inline-block mb-3">
                            <div className="w-[120px] h-[120px] flex items-center justify-center mx-auto">
                                {loading ? (
                                    <Skeleton width="100%" height="100%" />
                                ) : (
                                    <QRCode
                                        size={256}
                                        style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                                        value={zatcaResponse?.qrcode || ""}
                                        viewBox={`0 0 256 256`}
                                    />
                                )}
                            </div>
                        </div>
                    </div>

                    {!loading && data?.zatca_response?.message && (
                        <div className="p-3 bg-blue-50/50 border border-blue-100 rounded text-[11px] font-medium text-blue-700 leading-relaxed">
                            {data.zatca_response.message}
                        </div>
                    )}
                    {loading && <Skeleton width="100%" height="32px" />}
                </div>

                <div className="lg:w-2/3 bg-white border border-gray-100 rounded p-3 shadow-sm">
                    <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-50">
                        {loading ? <Skeleton shape="circle" size="14px" /> : <FaServer className="text-gray-400" size={14} />}
                        {loading ? <Skeleton width="120px" height="12px" /> : <span className="text-[11px] font-bold text-gray-600 uppercase tracking-wide">{t("ZATCA Validation Results")}</span>}
                    </div>

                    <div className="space-y-4">
                        {loading ? (
                            <>
                                <LogTable title={t("Critical Errors")} colorClass="text-red-600" icon={FaTimesCircle} />
                                <LogTable title={t("Warnings")} colorClass="text-yellow-600" icon={FaExclamationTriangle} />
                            </>
                        ) : validationResults ? (
                            <>
                                <LogTable
                                    items={validationResults.ErrorMessages}
                                    type="error"
                                    title={t("Critical Errors")}
                                    colorClass="text-red-600"
                                    icon={FaTimesCircle}
                                />
                                <LogTable
                                    items={validationResults.WarningMessages}
                                    type="warning"
                                    title={t("Warnings")}
                                    colorClass="text-yellow-600"
                                    icon={FaExclamationTriangle}
                                />

                                {(!validationResults.ErrorMessages?.length && !validationResults.WarningMessages?.length && !validationResults.InfoMessages?.length) && (
                                    <div className="py-20 text-center">
                                        <FaCheckCircle className="text-green-500 mx-auto mb-2" size={32} />
                                        <p className="text-[12px] font-bold text-gray-400 uppercase tracking-wider">{t("All Checks Passed")}</p>
                                        <p className="text-[10px] text-gray-400 mt-1">{t("No errors or warnings returned by ZATCA")}</p>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="py-20 text-center text-gray-300 italic text-[12px]">
                                {t("No validation data available for this record")}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ZatcaHistoryModal;
